/**
 * 卡墙兜底卡「我自己描述」(V1 §4.1)。
 *
 * ═══ 为什么它是一张卡,而不是页头那个主按钮 ═══════════════════════════════════
 * 改之前「自由创作」是页头的**主按钮**(`PosterDashboardPage` 三按钮之一),视觉权重压过
 * 整面卡墙 —— 但它其实是**没有目的时**的兜底:用户已经知道自己要什么,才轮得到它。
 * 摆在卡墙末尾一张「都不是?我自己描述」更诚实,也让卡墙成为唯一的选择面。
 *
 * ═══ 为什么不套 `SceneCapCard` ═══════════════════════════════════════════════
 * 那张卡的形制是「人物 + 真产物样图 + 形制图示」,而自由创作**没有固定产物**:
 * 没有能代表它的真样图(它出什么全看用户写什么)。硬塞进去要么给它配一张假样图
 * (违反「有真出品才上卡」铁律),要么留一个空舞台。所以按设计稿的 `.free-card`:
 * 同样的卡形(共用 `CARD_WALL_RADIUS` 形状锁 + `.scene-stage` 舞台比例),但舞台放
 * **人物**而不是样图 —— 「统一规格」指的是同一套规格语言,不是硬套同一个组件。
 *
 * ★2026-07-31:主角图补上了(`poster_free_text` / `poster_free_i2i` × 三皮肤)。
 *   这两张卡刚被提到卡墙首位(见 `PosterHubSections`),而它们原来是全墙**唯二**
 *   没有人物的卡 —— 摆在最显眼的位置上,那个虚线占位框就成了「这两张还没做完」的信号。
 *   ★没有样图仍然成立:舞台上只有人物,不放产物图。缺图静默降级(背景图 404 什么都不显示,
 *     卡其余部分照常),所以素材没到位也不会破版。
 */
import type { KeyboardEvent } from "react";
import { ArrowRight, ImagePlus, PencilLine } from "lucide-react";

import {
  POSTER_DEFAULT_TIER,
  perImage,
} from "@/entities/scene-card/model/creditRates";
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { figureOf } from "@/entities/scene-card/model/figures";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";

/**
 * 2026-07-28 方案 B —— 「拿一张图改」从创建页的一个 tab 独立成兜底区的第二张卡。
 *
 * ★原来它跟「爆点组图 / 多比例套图」平级放在创建页的段控里,但那些是**产出形态**,
 *   而改图是**「底图从哪来」** —— 不是一个维度。后果是改图态下半个页面的控件对它
 *   毫无意义(产品库 / 画风 / 配色 / 字体)。用户原话:「那个改图存在的意义是什么」。
 */
const VARIANTS = {
  text: {
    icon: PencilLine,
    /** 主角图 key —— `/scene-figures/<skin>/<figKey>.webp`,与场景卡同一套命名。 */
    figKey: "poster_free_text",
    title: "我自己描述",
    usage: "用一句话说要什么,AI 直接画",
    desc: "从零画一张;品牌 logo、二维码可叠。",
    tags: ["文字生图"],
    cta: "立即创作",
  },
  i2i: {
    icon: ImagePlus,
    figKey: "poster_free_i2i",
    title: "拿一张图改",
    usage: "上传一张图,说要改成什么样",
    desc: "换背景 / 风格重绘 / 保构图改字;可圈选局部只改一块。",
    tags: ["图改图", "局部重绘"],
    cta: "去改图",
  },
} as const;

export function PosterFreeCard({
  onOpen,
  variant = "text",
}: {
  onOpen: () => void;
  variant?: keyof typeof VARIANTS;
}) {
  const v = VARIANTS[variant];
  const Icon = v.icon;
  const skin = useActiveSkin();
  // 兜底卡的 provider 走海报页默认(OPENAI)+ 默认档 —— 与 catalog.ts 那张卡同一口径
  const { book } = usePriceBook();
  const perImageCredits = book.unit(SKU.posterImage, imageVariant("openai", POSTER_DEFAULT_TIER));
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      data-poster-free-card
      className={cn(
        "scene-cap group relative flex cursor-pointer flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS
      )}
    >
      {/* 舞台:人物铺满(与 SceneCapCard 的 .scene-fig 同一套取景 —— 素材四周烘的就是
          该皮肤 --card,所以底要用 bg-card,bg-muted 会在人物边缘露出一圈硬边)。
          图标退成人物之上的小角标:缺图时它就是唯一的形制线索,有图时也不碍事。 */}
      <div className="scene-stage relative w-full overflow-hidden bg-card">
        <span
          className="absolute inset-0 z-10 bg-cover bg-[position:center_14%] bg-no-repeat"
          style={{ backgroundImage: `url(${figureOf(skin, v.figKey)})` }}
          aria-hidden
        />
        <span
          className="absolute bottom-2 left-2 z-20 grid h-8 w-8 place-items-center rounded-[calc(var(--radius)*0.7)] bg-card/85 text-muted-foreground backdrop-blur-sm"
          aria-hidden
        >
          <Icon className="h-4 w-4" strokeWidth={1.6} />
        </span>
      </div>

      <div className="relative z-30 flex flex-1 flex-col gap-1 p-3">
        {/* h3 与场景卡同级:读屏用户按标题在卡墙里跳转时,兜底卡不能掉出层级 */}
        <h3 className="text-sm font-[650] tracking-[-0.012em]">{v.title}</h3>
        <div className="text-xs font-medium leading-snug text-foreground/[0.86]">{v.usage}</div>
        <p className="line-clamp-2 text-2xs leading-[1.62] text-muted-foreground">{v.desc}</p>
        <div className="mt-auto flex flex-wrap gap-1 pt-1.5">
          {v.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border/60 bg-background/50 px-1.5 py-0.5 text-2xs text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="relative z-30 flex items-center justify-between border-t border-border/60 px-3 py-2">
        {/* 积分标同源**后端价目表** —— 兜底卡也是真出图,不能不报价;
            但也不能报一个可能过期的价,取不到就出骨架。 */}
        {perImageCredits == null ? (
          <span className="h-3 w-24 animate-pulse rounded bg-muted/60" aria-label="积分标加载中" />
        ) : (
          <span className="text-2xs text-muted-foreground">
            {perImage(perImageCredits)}({POSTER_DEFAULT_TIER} 默认档)
          </span>
        )}
        <span className="inline-flex items-center gap-0.5 text-2xs font-semibold text-primary">
          {v.cta}
          <ArrowRight className="scene-cta-arrow h-3 w-3" />
        </span>
      </div>
    </div>
  );
}
